import { useState, useEffect, useCallback } from "react";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Edge,
    ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
    Box,
    Container,
    Typography,
    Button,
    Stack,
    Paper,
    Alert,
    CircularProgress,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
} from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { CompanyHierarchyNode } from "src/api/client-api/company/useOrganizationHierarchy";
import {
    buildOrganizationTreeFromHierarchy,
    calculateLayout,
    toggleNodeCollapseForHierarchy,
    getHierarchyStatsFromNodes,
    collapseAllNodes,
    CompanyNode as CompanyNodeType,
    CompanyEdge,
} from "src/utils/orgChartUtils";
import { CompanyNode } from "./CompanyNode";
import { useTranslation } from "react-i18next";

// Types de nœuds personnalisés
const nodeTypes: any = {
    company: CompanyNode,
};

export interface OrganizationChartProps {
    hierarchyData?: CompanyHierarchyNode[];
    selectedCompanyId?: number;
    onCompanySelect?: (companyId: number) => void;
    height?: number;
    showControls?: boolean;
    editable?: boolean;
}

export const OrganizationChart = ({
    hierarchyData,
    selectedCompanyId,
    onCompanySelect,
    height = 600,
    showControls = true,
    editable = false,
}: OrganizationChartProps) => {
    const { t } = useTranslation();
    const [nodes, setNodes, onNodesChange] = useNodesState<CompanyNodeType>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<CompanyEdge>([]);
    const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
    const [layoutDirection, setLayoutDirection] = useState<"TB" | "LR">("TB");
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [centerTrigger, setCenterTrigger] = useState(0);

    const updateLayout = useCallback(
        (companies: any[], collapsedNodes: Set<string>) => {
            if (!companies || companies.length === 0) return;

            const treeState = buildOrganizationTreeFromHierarchy(companies, collapsedNodes);
            const layoutedTree = calculateLayout(treeState.nodes, treeState.edges, layoutDirection);

            setNodes(layoutedTree.nodes);
            setEdges(layoutedTree.edges);
        },
        [layoutDirection],
    );

    useEffect(() => {
        if (hierarchyData) {
            updateLayout(hierarchyData, collapsedNodes);
        }
    }, [hierarchyData, collapsedNodes, updateLayout, centerTrigger]);

    const handleToggleCollapse = (nodeId: string) => {
        if (!hierarchyData) return;

        const newTreeState = toggleNodeCollapseForHierarchy(nodeId, hierarchyData, collapsedNodes);
        setCollapsedNodes(newTreeState.collapsedNodes);
    };

    const handleExpandAll = () => {
        if (!hierarchyData) return;
        setCollapsedNodes(new Set());
        setTimeout(() => {
            updateLayout(hierarchyData, new Set());
        }, 0);
    };

    const handleCollapseAll = () => {
        if (!hierarchyData) return;
        const treeState = collapseAllNodes(hierarchyData);
        setCollapsedNodes(treeState.collapsedNodes);
        setTimeout(() => {
            updateLayout(hierarchyData, treeState.collapsedNodes);
        }, 0);
    };

    const handleNodeClick = (company: any) => {
        const nodeId = `company-${company.id}`;
        setSelectedNodeId(nodeId);

        if (onCompanySelect) {
            onCompanySelect(company.id);
        }
    };

    const onConnect = (params: Connection | Edge) => {
        if (editable) {
            setEdges((eds) => addEdge(params, eds));
        }
    };

    const handleLayoutDirectionChange = (direction: "TB" | "LR") => {
        setLayoutDirection(direction);
    };

    const centerView = () => {
        setCenterTrigger((prev) => prev + 1);
    };

    const exportChart = () => {};

    const stats = hierarchyData ? getHierarchyStatsFromNodes(hierarchyData) : null;

    if (hierarchyData === undefined) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" height={height}>
                    <CircularProgress />
                    <Typography variant="h6" sx={{ ml: 2 }}>
                        {t("company.loadingOrganizationChart", "Chargement de l'organigramme...")}
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (hierarchyData === null) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">
                    {t(
                        "company.errorLoadingOrganizationChart",
                        "Erreur lors du chargement des données de l'organigramme.",
                    )}
                </Alert>
            </Container>
        );
    }

    if (hierarchyData.length === 0) {
        return (
            <Container maxWidth="lg">
                <Alert severity="info">
                    {t("company.noCompanyFound", "Aucune entreprise trouvée pour construire l'organigramme.")}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Stack spacing={3}>
                {/* En-tête avec contrôles */}
                {showControls && (
                    <Paper sx={{ p: 2 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={2}
                        >
                            {/* Statistiques */}
                            <Stack direction="row" spacing={2}>
                                {stats && (
                                    <>
                                        <Chip
                                            label={`${stats.totalCompanies} ${t("company.totalCompanies", "entreprises")}`}
                                        />
                                        <Chip
                                            label={`${stats.rootCompanies} ${t("company.rootCompanies", "racines")}`}
                                        />
                                        <Chip label={`${stats.maxDepth} ${t("company.maxDepth", "niveaux")}`} />
                                    </>
                                )}
                            </Stack>

                            {/* Contrôles de layout */}
                            <FormControl>
                                <FormLabel component="legend" sx={{ fontSize: "0.8rem" }}>
                                    {t("company.direction", "Direction")}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={layoutDirection}
                                    onChange={(e) => handleLayoutDirectionChange(e.target.value as "TB" | "LR")}
                                >
                                    <FormControlLabel
                                        value="TB"
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    "&.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        }
                                        label={t("company.vertical", "Vertical")}
                                        sx={{
                                            "& .MuiFormControlLabel-label": { fontSize: "0.8rem", color: "#393A36" },
                                        }}
                                    />
                                    <FormControlLabel
                                        value="LR"
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    "&.Mui-checked": {
                                                        color: (theme) => theme.palette.primary[600],
                                                    },
                                                }}
                                            />
                                        }
                                        label={t("company.horizontal", "Horizontal")}
                                        sx={{
                                            "& .MuiFormControlLabel-label": { fontSize: "0.8rem", color: "#393A36" },
                                        }}
                                    />
                                </RadioGroup>
                            </FormControl>

                            {/* Boutons d'action */}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={handleExpandAll}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        border: (theme) => `1px solid ${theme.palette.primary[600]}`,
                                        color: (theme) => theme.palette.primary[600],
                                        textTransform: "none",
                                        "&:hover": {
                                            border: (theme) => `1px solid ${theme.palette.primary[700]}`,
                                            color: (theme) => theme.palette.primary[700],
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    {t("company.expandAll", "Tout déplier")}
                                </Button>
                                <Button
                                    startIcon={<ChevronRightIcon />}
                                    onClick={handleCollapseAll}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        border: (theme) => `1px solid ${theme.palette.primary[600]}`,
                                        color: (theme) => theme.palette.primary[600],
                                        textTransform: "none",
                                        "&:hover": {
                                            border: (theme) => `1px solid ${theme.palette.primary[700]}`,
                                            color: (theme) => theme.palette.primary[700],
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    {t("company.collapseAll", "Tout plier")}
                                </Button>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={() => updateLayout(hierarchyData, new Set())}
                                    size="small"
                                    sx={{
                                        color: "#393A36",
                                        textTransform: "none",
                                    }}
                                >
                                    {t("company.reset", "Réinitialiser")}
                                </Button>
                                <Button
                                    startIcon={<CenterFocusStrongIcon />}
                                    onClick={centerView}
                                    size="small"
                                    sx={{
                                        color: "#393A36",
                                        textTransform: "none",
                                    }}
                                >
                                    {t("company.center", "Centrer")}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                )}

                {/* Zone de l'organigramme */}
                <Box
                    sx={{
                        height: height,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <ReactFlowProvider>
                        <ReactFlow
                            key={`reactflow-${centerTrigger}`}
                            nodes={nodes.map((node) => ({
                                ...node,
                                selected: node.id === selectedNodeId,
                                data: {
                                    ...node.data,
                                    onToggleCollapse: handleToggleCollapse,
                                    onNodeClick: handleNodeClick,
                                },
                            }))}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            nodeTypes={nodeTypes}
                            fitView
                            fitViewOptions={{
                                padding: 0.2,
                                includeHiddenNodes: false,
                            }}
                            minZoom={0.1}
                            maxZoom={2}
                            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                            connectionLineType={"smoothstep" as any}
                            deleteKeyCode={editable ? "Delete" : null}
                        >
                            <Background />
                            <Controls />
                            <MiniMap
                                nodeStrokeColor={(n) => {
                                    if (n.type === "company") return "#1976d2";
                                    return "#eee";
                                }}
                                nodeColor={(n) => {
                                    if (n.type === "company") return "#fff";
                                    return "#fff";
                                }}
                                nodeBorderRadius={2}
                            />
                        </ReactFlow>
                    </ReactFlowProvider>
                </Box>
            </Stack>
        </Container>
    );
};
